import { useState } from "react";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { SectionHead, GoldButton, GhostButton, inputCls } from "./shared";

function RoleRow({ role, holders, api, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(role.label);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!label.trim() || label.trim() === role.label) { setEditing(false); return; }
    setBusy(true);
    try { await api.patch(`/admin/pioneers/roles/${role.key}`, { label: label.trim() }); toast.success("Sub-role renamed"); setEditing(false); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the sub-role "${role.label}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await api.delete(`/admin/pioneers/roles/${role.key}`); toast.success("Sub-role deleted"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } finally { setBusy(false); }
  };
  const toggleCap = async (field) => {
    setBusy(true);
    try { await api.patch(`/admin/pioneers/roles/${role.key}`, { [field]: !role[field] }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); } finally { setBusy(false); }
  };

  return (
    <tr data-testid={`pioneer-role-row-${role.key}`} className="border-t border-[#262626] hover:bg-[#1A1A1A]">
      <td className="py-3 pr-4">
        {editing ? (
          <div className="flex items-center gap-2">
            <input data-testid={`pioneer-role-rename-input-${role.key}`} value={label} onChange={(e) => setLabel(e.target.value)} className={`${inputCls} max-w-[240px]`} autoFocus onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} />
            <button data-testid={`pioneer-role-rename-save-${role.key}`} onClick={save} disabled={busy} className="text-[#C9A84C] hover:text-[#F5F5F5]"><Check size={16} /></button>
            <button onClick={() => { setEditing(false); setLabel(role.label); }} className="text-[#888] hover:text-[#F5F5F5]"><X size={16} /></button>
          </div>
        ) : (
          <div>
            <div className="text-[#F5F5F5] font-medium" data-testid={`pioneer-role-label-${role.key}`}>{role.label}</div>
            <div className="text-xs text-[#888888] mt-0.5 max-w-md">{role.summary}</div>
          </div>
        )}
      </td>
      <td className="py-3 pr-4 text-sm text-[#D4D4D4]" data-testid={`pioneer-role-holders-${role.key}`}>{holders}</td>
      <td className="py-3 pr-4">
        <button data-testid={`pioneer-role-approve-${role.key}`} onClick={() => toggleCap("can_approve")} disabled={busy}
          className={`text-[11px] font-grotesk uppercase tracking-[0.12em] px-2 py-1 rounded-sm border ${role.can_approve ? "border-[#C9A84C] text-[#C9A84C]" : "border-[#2A2A2A] text-[#6B6B6B]"}`}>{role.can_approve ? "Approves" : "No approval"}</button>
      </td>
      <td className="py-3 pr-4">
        <button data-testid={`pioneer-role-initiate-${role.key}`} onClick={() => toggleCap("can_initiate")} disabled={busy}
          className={`text-[11px] font-grotesk uppercase tracking-[0.12em] px-2 py-1 rounded-sm border ${role.can_initiate ? "border-[#C9A84C] text-[#C9A84C]" : "border-[#2A2A2A] text-[#6B6B6B]"}`}>{role.can_initiate ? "Initiates" : "No initiation"}</button>
      </td>
      <td className="py-3 text-right whitespace-nowrap">
        <GhostButton testid={`pioneer-role-rename-${role.key}`} onClick={() => setEditing(true)} disabled={busy}><Pencil size={13} /> Rename</GhostButton>
        <GhostButton testid={`pioneer-role-delete-${role.key}`} onClick={remove} disabled={busy || holders > 0} danger className="ml-2" title={holders > 0 ? "Reassign members before deleting" : "Delete sub-role"}><Trash2 size={13} /></GhostButton>
      </td>
    </tr>
  );
}

export function RoleManager({ data, api, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const holdersFor = (key) => data.members.filter((m) => m.role_key === key).length;

  const create = async () => {
    if (label.trim().length < 2) return toast.error("Enter a sub-role name");
    setBusy(true);
    try { await api.post("/admin/pioneers/roles", { label: label.trim(), summary }); toast.success(`Sub-role "${label.trim()}" added`); setLabel(""); setSummary(""); setAdding(false); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add sub-role"); } finally { setBusy(false); }
  };

  return (
    <section className="admin-card" data-testid="pioneer-role-manager">
      <SectionHead kicker="Sub-role manager" title="Pioneer Sub-roles"
        action={<GoldButton testid="pioneer-role-add-toggle" onClick={() => setAdding((v) => !v)}><Plus size={14} /> Add sub-role</GoldButton>} />
      {adding && (
        <div className="mb-5 grid md:grid-cols-[1fr_2fr_auto] gap-3 items-start border border-[#333333] rounded-sm p-4 bg-[#141414]" data-testid="pioneer-role-add-form">
          <input data-testid="pioneer-role-add-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Sub-role name, e.g. Treasurer" className={inputCls} />
          <input data-testid="pioneer-role-add-summary" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="One-line description (optional)" className={inputCls} />
          <GoldButton testid="pioneer-role-add-submit" onClick={create} disabled={busy}>Create</GoldButton>
          <p className="md:col-span-3 text-xs text-[#888888]">New sub-roles start with <span className="text-[#F5F5F5]">No Access</span> on every module. Set permissions in the matrix below.</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#C9A84C]">
              <th className="pb-2 pr-4">Sub-role</th><th className="pb-2 pr-4">Members</th><th className="pb-2 pr-4">Approval</th><th className="pb-2 pr-4">Initiation</th><th className="pb-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>{data.roles.map((r) => <RoleRow key={r.key} role={r} holders={holdersFor(r.key)} api={api} onChanged={onChanged} />)}</tbody>
        </table>
      </div>
    </section>
  );
}
